import React, { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Layers, AlertTriangle } from 'lucide-react';

const C2_SelectLevels = () => {
  const { dataC, updateDataRaw, nextStep, previousStep } = useWizard();
  const [selectedLevels, setSelectedLevels] = useState(dataC?.levels || []);

  useEffect(() => {
    if (dataC?.levels) {
      setSelectedLevels(dataC.levels);
    }
  }, [dataC?.levels]);

  const analysisLevels = [
    { id: 'individual', level: 'Individual', unit: 'Person', examples: 'Personality, attitudes, behavior' },
    { id: 'interpersonal', level: 'Interpersonal', unit: 'Dyad/Group', examples: 'Communication, conflict, trust' },
    { id: 'team', level: 'Team', unit: 'Work group', examples: 'Team cohesion, performance' },
    { id: 'organizational', level: 'Organizational', unit: 'Company', examples: 'Culture, structure, strategy' },
    { id: 'institutional', level: 'Institutional', unit: 'Industry/Sector', examples: 'Norms, regulations, standards' },
    { id: 'societal', level: 'Societal', unit: 'Nation/Culture', examples: 'Values, laws, economic systems' }
  ];

  const toggleLevel = (levelId) => {
    const updated = selectedLevels.includes(levelId)
      ? selectedLevels.filter(id => id !== levelId)
      : [...selectedLevels, levelId];
    setSelectedLevels(updated);
    updateDataRaw({ 'wizardC.levels': updated });
  };

  const handleContinue = () => {
    updateDataRaw({ 'wizardC.levels': selectedLevels });
    nextStep();
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon levels-icon">
          <Layers size={32} />
        </div>
        <div>
          <h2>Step 2: Select Framework Levels</h2>
          <p className="step-description">Choose the level(s) at which your framework operates</p>
        </div>
      </div>

      <div className="step-content">
        <div className="levels-recommendation">
          <span className="recommendation-icon">💡</span>
          <span>We recommend selecting <strong>no more than 3 levels</strong> to keep your framework focused and manageable.</span>
        </div>
        
        <div className="levels-counter">
          <span className={`counter-badge ${selectedLevels.length > 3 ? 'warning' : ''}`}>
            {selectedLevels.length} of 6 selected
          </span>
          {selectedLevels.length > 3 && (
            <span className="counter-warning">
              <AlertTriangle size={16} />
              Consider reducing to 3 or fewer
            </span>
          )}
        </div>

        <div className="levels-table">
          <div className="levels-header">
            <span className="col-select">Select</span>
            <span className="col-level">Level</span>
            <span className="col-unit">Unit of Analysis</span>
            <span className="col-examples">Example Constructs</span>
          </div>
          {analysisLevels.map((level, index) => (
            <div 
              key={level.id} 
              className={`levels-row ${selectedLevels.includes(level.id) ? 'selected' : ''}`}
              onClick={() => toggleLevel(level.id)}
            >
              <span className="col-select">
                <input
                  type="checkbox"
                  checked={selectedLevels.includes(level.id)}
                  onChange={() => toggleLevel(level.id)}
                  className="level-checkbox"
                />
              </span>
              <span className="col-level">
                <span className="level-number">{index + 1}</span>
                {level.level}
              </span>
              <span className="col-unit">{level.unit}</span>
              <span className="col-examples">{level.examples}</span>
            </div>
          ))}
        </div>

        {selectedLevels.length >= 2 && (
          <div className="cross-level-note">
            <h4>Cross-Level Framework</h4>
            <p>
              You've selected multiple levels. Your framework will show how factors at one level 
              influence outcomes at another (cross-level or multi-level analysis).
            </p>
          </div>
        )}
      </div>

      <NavigationButtons
        onBack={previousStep}
        onContinue={handleContinue}
        canContinue={selectedLevels.length >= 1}
        continueLabel="Continue to Theoretical Foundations"
      />
    </div>
  );
};

export default C2_SelectLevels;
